/**
 * Application root: providers, route table and the authenticated shell.
 *
 * The client-side guard is a convenience only. Every protected page still needs
 * a valid token, and the Worker re-verifies it on every request.
 */

import { useEffect, useMemo } from 'react';
import type { ReactNode } from 'react';
import type { Entitlements, QuotaStatus } from '@core/types';
import { RouterProvider, resolveRoute, useRouter, Link, type RouteDefinition } from './lib/router';
import { SessionProvider, useSession } from './lib/session';
import { useMe } from './lib/hooks';
import { Layout, PageHead } from './components/Layout';
import { Alert, Spinner } from './components/ui';
import { LandingPage, PricingPage, PrivacyPage, TermsPage } from './pages/PublicPages';
import { SignInPage } from './pages/SignIn';
import { NewDiagnosisPage } from './pages/NewDiagnosis';
import { DiagnosisResultPage } from './pages/DiagnosisResult';
import { HistoryPage } from './pages/History';
import { SpecsPage } from './pages/Specs';
import { BillingPage } from './pages/Billing';
import { AccountPage } from './pages/Account';
import { SharedReportPage, NotFoundPage } from './pages/SharedReport';

/** Patterns that carry dynamic segments. Kept in one place for param extraction. */
const PARAM_PATTERNS = ['/r/:token', '/app/diagnoses/:id'] as const;

function extractParams(path: string): Record<string, string> {
  const params: Record<string, string> = {};
  const pathParts = path.split('/').filter(Boolean);
  for (const pattern of PARAM_PATTERNS) {
    const patternParts = pattern.split('/').filter(Boolean);
    if (patternParts.length !== pathParts.length) continue;
    for (let i = 0; i < patternParts.length; i += 1) {
      const expected = patternParts[i]!;
      if (expected.startsWith(':')) {
        params[expected.slice(1)] = decodeURIComponent(pathParts[i]!);
      }
    }
  }
  return params;
}

/* -------------------------------------------------------------------------- */
/* Authenticated shell                                                         */
/* -------------------------------------------------------------------------- */

function LoadingPanel({ label }: { label: string }) {
  return (
    <div className="card row">
      <Spinner label={label} /> <span>{label}</span>
    </div>
  );
}

function SignInRequired() {
  const { path } = useRouter();
  return (
    <div className="container-narrow">
      <PageHead
        title="Sign in required"
        description="API Rescue Desk refuses anonymous diagnoses. Sign in with Google to continue."
      />
      <div className="row">
        <Link className="button button--primary" to={`/signin?next=${encodeURIComponent(path)}`}>
          Sign in with Google
        </Link>
        <Link className="button" to="/pricing">
          See pricing
        </Link>
      </div>
    </div>
  );
}

interface AccountState {
  quota: QuotaStatus;
  entitlements: Entitlements;
}

/**
 * Wraps an authenticated page. The plan and quota always come from `/me`, which
 * the Worker resolves from the database, so the browser never supplies them.
 */
function AuthenticatedArea({ children }: { children: (state: AccountState) => ReactNode }) {
  const { status } = useSession();
  const me = useMe();

  if (status === 'loading') return <LoadingPanel label="Checking your session…" />;
  if (status !== 'signed_in') return <SignInRequired />;
  if (me.loading) return <LoadingPanel label="Loading your plan and quota…" />;

  if (me.error || !me.me) {
    return (
      <Alert tone="error" title="Your account details could not be loaded">
        <p>{me.error?.message ?? 'The API did not return your account details.'}</p>
        <button type="button" className="button--small" onClick={me.reload}>
          Try again
        </button>
      </Alert>
    );
  }

  return <>{children({ quota: me.me.quota, entitlements: me.me.entitlements })}</>;
}

/* -------------------------------------------------------------------------- */
/* Routing                                                                     */
/* -------------------------------------------------------------------------- */

function RouteOutlet() {
  const { path } = useRouter();
  const me = useMe();
  const params = useMemo(() => extractParams(path), [path]);

  const routes: RouteDefinition[] = [
    { pattern: '/', element: <LandingPage /> },
    { pattern: '/pricing', element: <PricingPage /> },
    { pattern: '/privacy', element: <PrivacyPage /> },
    { pattern: '/terms', element: <TermsPage /> },
    { pattern: '/signin', element: <SignInPage /> },
    { pattern: '/r/:token', element: <SharedReportPage token={params.token ?? ''} /> },
    {
      pattern: '/app',
      element: (
        <AuthenticatedArea>
          {({ quota, entitlements }) => (
            <NewDiagnosisPage entitlements={entitlements} quota={quota} onQuotaChange={me.reload} />
          )}
        </AuthenticatedArea>
      ),
    },
    {
      pattern: '/app/diagnoses',
      element: (
        <AuthenticatedArea>{({ quota }) => <HistoryPage quota={quota} />}</AuthenticatedArea>
      ),
    },
    {
      pattern: '/app/diagnoses/:id',
      element: (
        <AuthenticatedArea>
          {({ quota, entitlements }) => (
            <DiagnosisResultPage
              id={params.id ?? ''}
              entitlements={entitlements}
              quota={quota}
              onQuotaChange={me.reload}
            />
          )}
        </AuthenticatedArea>
      ),
    },
    {
      pattern: '/app/specs',
      element: (
        <AuthenticatedArea>
          {({ entitlements }) => <SpecsPage entitlements={entitlements} />}
        </AuthenticatedArea>
      ),
    },
    {
      pattern: '/app/billing',
      element: (
        <AuthenticatedArea>
          {({ quota, entitlements }) =>
            me.me ? (
              <BillingPage
                entitlements={entitlements}
                quota={quota}
                subscription={me.me.subscription}
                billing={me.me.billing}
                onReload={me.reload}
              />
            ) : null
          }
        </AuthenticatedArea>
      ),
    },
    {
      pattern: '/app/account',
      element: (
        <AuthenticatedArea>
          {({ quota, entitlements }) =>
            me.me ? (
              <AccountPage
                profile={me.me.profile}
                entitlements={entitlements}
                quota={quota}
                onReload={me.reload}
              />
            ) : null
          }
        </AuthenticatedArea>
      ),
    },
  ];

  const resolution = resolveRoute(routes, path);

  useEffect(() => {
    if (resolution.matchedPattern === '/r/:token') {
      document.title = 'Shared incident report — API Rescue Desk';
      return;
    }
    const titles: Record<string, string> = {
      '/': 'API Rescue Desk — fix failed API requests in under two minutes',
      '/pricing': 'Pricing — API Rescue Desk',
      '/privacy': 'Privacy — API Rescue Desk',
      '/terms': 'Terms — API Rescue Desk',
      '/signin': 'Sign in — API Rescue Desk',
      '/app': 'New diagnosis — API Rescue Desk',
      '/app/diagnoses': 'Diagnosis history — API Rescue Desk',
      '/app/specs': 'Saved OpenAPI specifications — API Rescue Desk',
      '/app/billing': 'Billing and plan — API Rescue Desk',
      '/app/account': 'Account settings — API Rescue Desk',
    };
    document.title = titles[resolution.matchedPattern ?? ''] ?? 'API Rescue Desk';
  }, [resolution.matchedPattern]);

  return <>{resolution.element ?? <NotFoundPage />}</>;
}

/* -------------------------------------------------------------------------- */
/* Root                                                                        */
/* -------------------------------------------------------------------------- */

export function App() {
  return (
    <RouterProvider>
      <SessionProvider>
        <Layout>
          <RouteOutlet />
        </Layout>
      </SessionProvider>
    </RouterProvider>
  );
}
